import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Stack, Step, StepLabel, Stepper, TextField, Typography,
} from '@mui/material';
import CodeField from './CodeField';
import TotpEnrollment from './TotpEnrollment';
import { friendlyAuthError } from '../cognito';

const STEPS = ['Confirm it’s you', 'Pair new app', 'Done'];

// Account-page card for switching authenticator apps (e.g. Google
// Authenticator -> 1Password). The industry-standard sequence:
//   1. re-authenticate with password + a code from the CURRENT app,
//   2. pair the new app (QR or setup key) and prove it with one of its codes,
//   3. only then does the switch happen; the old app's codes stop working.
// Cancelling before step 2 completes leaves the current app in place.
const ChangeAuthenticator = ({ auth, appName }) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [secret, setSecret] = useState('');
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { auth.getEmail().then(setEmail, () => {}); }, [auth]);

  const reset = () => {
    setActive(0); setPassword(''); setCode(''); setSecret(''); setUser(null); setError(''); setBusy(false);
  };
  const close = () => { setOpen(false); reset(); };

  const run = (action, errorOptions) => async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await action();
    } catch (err) {
      setError(friendlyAuthError(err, errorOptions));
    } finally {
      setBusy(false);
    }
  };

  const confirmIdentity = run(async () => {
    const fresh = await auth.reauthenticate(password, code);
    setSecret(await auth.startTotpEnrollment(fresh));
    setUser(fresh);
    setPassword(''); setCode('');
    setActive(1);
  }, { passwordOnly: true });

  const pairNewApp = run(async () => {
    await auth.finishTotpEnrollment(user, code);
    setCode('');
    setActive(2);
  });

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="h6" component="h2">Two-factor authentication</Typography>
          <Chip label="On" color="success" size="small" />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Signing in to {appName} needs your password and a 6-digit code from your authenticator app.
          Moving to a new app or phone? Pair it here — your current app keeps working until the new one is confirmed.
        </Typography>
        <Button variant="contained" onClick={() => setOpen(true)}>Change authenticator app</Button>
      </CardContent>

      <Dialog open={open} onClose={busy ? undefined : close} fullWidth maxWidth="xs" aria-labelledby="change-authenticator-title">
        <DialogTitle id="change-authenticator-title">Change authenticator app</DialogTitle>
        <DialogContent>
          <Stepper activeStep={active} alternativeLabel sx={{ mb: 2 }}>
            {STEPS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
          </Stepper>

          {error && <Alert severity="error" sx={{ mb: 2 }} role="alert">{error}</Alert>}

          {active === 0 && (
            <form id="change-authenticator-form" onSubmit={confirmIdentity}>
              <Typography variant="body2">
                Enter your password and a code from your <strong>current</strong> authenticator app.
              </Typography>
              {/* Hidden username so password managers match the right login. */}
              <input type="text" name="username" autoComplete="username" value={email} readOnly hidden />
              <TextField fullWidth label="Password" type="password" autoComplete="current-password" value={password}
                onChange={(e) => setPassword(e.target.value)} margin="normal" required autoFocus />
              <CodeField value={code} onChange={setCode} label="Code from current app" id="current-code" />
            </form>
          )}

          {active === 1 && (
            <form id="change-authenticator-form" onSubmit={pairNewApp}>
              <TotpEnrollment issuer={appName} account={email} secret={secret} code={code} onCodeChange={setCode} />
              <Typography variant="caption" color="text.secondary">
                Until you confirm, your current app still works.
              </Typography>
            </form>
          )}

          {active === 2 && (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>Your new authenticator app is now active.</Alert>
              <Typography variant="body2">
                Codes from your old app no longer work. You can delete the {appName} entry from it.
                Next time you sign in, use the code from your new app.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {active < 2 && <Button onClick={close} disabled={busy}>Cancel</Button>}
          {active === 0 && (
            <Button type="submit" form="change-authenticator-form" variant="contained" disabled={busy}>
              {busy ? 'Checking…' : 'Continue'}
            </Button>
          )}
          {active === 1 && (
            <Button type="submit" form="change-authenticator-form" variant="contained" disabled={busy}>
              {busy ? 'Verifying…' : 'Confirm new app'}
            </Button>
          )}
          {active === 2 && <Button onClick={close} variant="contained">Done</Button>}
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ChangeAuthenticator;
